import useUser from '@/features/auth/hooks/useUser'
import { MEASURE_ID } from '@/types/user'

/**
 * Conversion factor: 1 kL (= 1 m³) = 0.264172 kGal (kilogallons).
 *
 * Water cost conversion:
 *   Metric → Imperial: Cost ($/kGal) = Cost ($/kL) ÷ 0.264172
 *   Imperial → Metric: Cost ($/kL)   = Cost ($/kGal) × 0.264172
 */
const KL_TO_KGAL: number = 0.264172

/**
 * Custom hook for converting water cost between metric and Imperial units.
 * The backend stores water cost as $/kL (or $/m³). For Imperial users,
 * the displayed cost will be converted to $/kGal (per kilogallon).
 */
export const useWaterCost = () => {
  const { data: user } = useUser()

  const isMetric = !user?.measure?.id || user?.measure?.id === MEASURE_ID.METRIC

  /**
   * Converts water cost from metric ($/kL or $/m³) to display unit.
   * For Metric users (measure.id === 1 or unset), no conversion is applied.
   * For Imperial users, converts $/kL to $/kGal.
   *
   * Formula: Cost ($/kGal) = Cost ($/kL) ÷ 0.264172
   *
   * @param value - Water cost value ($/kL) as a number or string.
   * @param isFixed - Number of decimals to fix the output (default: 2).
   * @returns The converted water cost as a string, or a number if no conversion is needed.
   */
  const convertFromMetric = (
    value: number | string,
    isFixed = 2,
  ): string | number => {
    const numericValue = Number(value)
    if (isNaN(numericValue)) return -1

    // If the user is using Metric, return the value as stored.
    if (isMetric) return numericValue

    // For Imperial users, convert from $/kL to $/kGal.
    let result = numericValue / KL_TO_KGAL

    // If result is an exact whole number, return it without forced decimal places
    const rounded = Math.round(result)
    if (Math.abs(result - rounded) < 1e-6) {
      return rounded.toString()
    }

    // If the value in database was stored with 2 or fewer decimals (e.g. legacy 2.64 or 2.91),
    // check if snapping to a whole number within 0.02 recovers the original input.
    const is2DecimalsOrLess =
      Math.abs(numericValue - Math.round(numericValue * 100) / 100) < 1e-9
    if (is2DecimalsOrLess && Math.abs(result - rounded) <= 0.02) {
      return rounded.toString()
    }

    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: isFixed,
      minimumFractionDigits: isFixed,
    })
      .format(result)
      .replace(/,/g, '')
  }

  const convertFromMetricRaw = (value: number | string): number => {
    const numericValue = Number(value)
    if (isNaN(numericValue)) return -1

    // If the user is using Metric, return the value as stored.
    if (isMetric) return numericValue

    // For Imperial users, convert from $/kL to $/kGal.
    let result = numericValue / KL_TO_KGAL

    const rounded = Math.round(result)
    if (Math.abs(result - rounded) < 1e-6) {
      return rounded
    }

    const is2DecimalsOrLess =
      Math.abs(numericValue - Math.round(numericValue * 100) / 100) < 1e-9
    if (is2DecimalsOrLess && Math.abs(result - rounded) <= 0.02) {
      return rounded
    }

    return Number(result.toFixed(8))
  }

  /**
   * Converts water cost from display unit back to metric for backend storage.
   * For Metric users, returns the value as is.
   * For Imperial users, converts $/kGal to $/kL without truncating precision.
   *
   * Formula: Cost ($/kL) = Cost ($/kGal) × 0.264172
   *
   * @param value - Water cost value ($/kGal) as a number or string.
   * @returns The converted water cost as a number for backend storage.
   */
  const convertToMetric = (
    value: number | string,
  ): number => {
    const numericValue = Number(value)
    if (isNaN(numericValue)) return -1

    // If the user is using Metric, return the value as is.
    if (isMetric) return numericValue

    // For Imperial users, convert from $/kGal to $/kL without truncating precision.
    const result = numericValue * KL_TO_KGAL
    return Number(result.toFixed(8))
  }

  const convertToMetricRaw = (value: number | string): number => {
    return convertToMetric(value)
  }

  return {
    convertFromMetric,
    convertToMetric,
    convertToMetricRaw,
    convertFromMetricRaw,
  }
}
